import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Phonelink, AdsClick, Insights, CallToAction, ChatBubbleOutline,
  SmartToy, RecordVoiceOver, LocalPhone, PhoneInTalk, ChevronLeft, ChevronRight,
  RocketLaunch, CreditCard,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserPreferencesProvider, useUserPreferences } from '../contexts/UserPreferencesContext';
import { PricingProvider, usePricing } from '../contexts/PricingContext';
import { createTab, getComparison } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  getSnapshotFromOffer,
  sortProductsByPriority,
} from '../utils/snapshotUtils';
import { exportToPDF, generatePDFFilename } from '../utils/exportUtils';
import {
  getComparisonRowLabel,
  getComparisonTierName,
} from '../utils/comparisonLabels';
import { getOfferValidityDate } from '../utils/dateUtils';
import { formatSnapshotPrice } from '../utils/pricingItemNormalizer';
import {
  buildCalculatorTemplateUrl,
  buildComparisonTemplatePayloads,
  getComparisonTemplateRegion,
} from '../utils/offerTemplateUtils';
import {
  getComparisonPlanLabel,
  getComparisonPlanSavings,
} from '../utils/comparisonSubscriptionDisplay';

// Landing components
import LandingHeader from '../components/landing/LandingHeader';
import SalesContactCard from '../components/landing/SalesContactCard';
import AdvantagesSection from '../components/landing/AdvantagesSection';
import ContactFooter from '../components/landing/ContactFooter';
import { MonobankIcon, ABankIcon } from '../components/icons/BankIcons';

const HEADING_FONT = "'Manrope', sans-serif";
const BODY_FONT = "'Inter', sans-serif";

// Types that can have multiple entries per offer (use full label as key)
const MULTI_ENTRY_TYPES = new Set(['phone-numbers', 'voice-traffic']);

const PRODUCT_ICONS = {
  'business-phone': Phonelink,
  'fmc': Phonelink,
  'call-tracking': AdsClick,
  'ai-conversation': Insights,
  'callback': CallToAction,
  'omnichannel-chat': ChatBubbleOutline,
  'eniq-ai': SmartToy,
  'voicebot': RecordVoiceOver,
  'phone-numbers': LocalPhone,
  'voice-traffic': PhoneInTalk,
};

const SectionTitle = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 5 }}>
    <Typography
      variant="h4"
      sx={{
        fontFamily: HEADING_FONT,
        fontWeight: 700,
        fontSize: { xs: '1.75rem', md: '1.875rem' },
        flexShrink: 0,
      }}
    >
      {children}
    </Typography>
    <Box
      sx={{
        height: 4,
        flex: 1,
        mx: 4,
        bgcolor: '#edeeef',
        borderRadius: 99,
        display: { xs: 'none', md: 'block' },
      }}
    >
      <Box sx={{ height: '100%', width: '33%', bgcolor: '#00c853', borderRadius: 99 }} />
    </Box>
  </Box>
);

// ---------- Summary Card (minimal) ----------
const SummaryCard = ({ offer, index, t, fmtPrice, isHighlighted }) => {
  const snapshot = getSnapshotFromOffer(offer);
  const totalPrice = snapshot?.total_monthly || offer?.total_price || 0;
  const products = snapshot?.individual_prices || [];
  const plans = snapshot?.subscription_plans || [];
  const recPlan = plans.find(p => p.isRecommended || p.is_recommended) || plans[0];
  const recPayment = recPlan ? (recPlan.paymentAmount || recPlan.payment_amount || 0) : 0;

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 1,
        bgcolor: 'white',
        border: isHighlighted ? '2px solid #006e2a' : '1px solid rgba(187,203,184,0.12)',
        textAlign: 'center',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.10)',
        },
      }}
    >
      <Typography
        sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: '1rem', color: '#00658d', mb: 1, minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {offer.tab_data?.name || `${t('offers.offer', 'Offer')} ${index + 1}`}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontWeight: 900,
            fontSize: '2rem',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #006e2a 0%, #00c853 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {fmtPrice(totalPrice)}
        </Typography>
        <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.8125rem', color: '#006e2a', fontWeight: 600 }}>
          {t('subscription.perMonth', '/mo')}
        </Typography>
      </Box>

      <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.75rem', color: '#3c4a3c', opacity: 0.6, mt: 0.5 }}>
        {t('offer.priceNote', 'annual payment, excl. VAT')}
      </Typography>

      {/* Product count + subscription summary */}
      <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.8125rem', color: '#3c4a3c', mt: 2 }}>
        {products.length} {t('comparison.products', 'products')}
        {recPlan && recPayment > 0 && (
          <> · {fmtPrice(recPayment)}{t('comparison.perYear', '/yr')}</>
        )}
      </Typography>

      <Button
        variant="contained"
        fullWidth
        onClick={() => window.open(`/offers/${offer.id}`, '_blank')}
        sx={{
          bgcolor: '#00c853',
          mt: 2,
          py: 1.25,
          fontFamily: BODY_FONT,
          fontWeight: 600,
          borderRadius: 1,
          textTransform: 'none',
          '&:hover': { bgcolor: '#00a040' },
        }}
      >
        {t('offers.detailedOffer', 'View Full Offer')}
      </Button>
    </Box>
  );
};

// ---------- Comparison Table ----------
export const OfferComparisonTable = ({ offers, t, fmtPrice }) => {
  // Collect all unique products across all offers
  const { allProducts, offerProductMaps } = useMemo(() => {
    const productMap = new Map(); // key -> { type, label, key }
    const maps = offers.map(offer => {
      const snapshot = getSnapshotFromOffer(offer);
      const items = snapshot?.individual_prices || [];
      const sorted = sortProductsByPriority(items);
      const m = new Map();
      sorted.forEach(item => {
        const type = item.productType;
        const label = item.label || item.name || type;
        const fullMatch = label.match(/^(.+?)\s*\((.+)\)$/);
        const baseName = fullMatch ? fullMatch[1] : label;

        if (MULTI_ENTRY_TYPES.has(type)) {
          // Each unique label gets its own row
          const rowKey = `${type}__${baseName}`;
          if (!productMap.has(rowKey)) {
            productMap.set(rowKey, { type, label: baseName, key: rowKey });
          }
          m.set(rowKey, item);
        } else {
          if (!productMap.has(type)) {
            productMap.set(type, { type, label: getComparisonRowLabel(type, label), key: type });
          }
          m.set(type, item);
        }
      });
      return m;
    });
    // Sort: BP/FMC first, phone-numbers grouped, voice-traffic grouped at the end
    const sortOrder = (type) => {
      if (type === 'business-phone') return 0;
      if (type === 'fmc') return 1;
      if (type === 'phone-numbers') return 98;
      if (type === 'voice-traffic') return 99;
      return 10;
    };
    const sorted = Array.from(productMap.values()).sort((a, b) => sortOrder(a.type) - sortOrder(b.type));
    return { allProducts: sorted, offerProductMaps: maps };
  }, [offers]);

  // Subscription data per offer (plans + discount info)
  const offerPlans = useMemo(() => {
    return offers.map(offer => {
      const snapshot = getSnapshotFromOffer(offer);
      return {
        plans: snapshot?.subscription_plans || [],
        customDiscount360: snapshot?.custom_discount_360 ?? 20,
        customDiscount180: snapshot?.custom_discount_180 ?? 0,
      };
    });
  }, [offers]);

  const isUa = (() => {
    const snap = getSnapshotFromOffer(offers[0]);
    return snap?.region === 'ua';
  })();

  const tableRef = React.useRef(null);
  const [canTableScrollLeft, setCanTableScrollLeft] = React.useState(false);
  const [canTableScrollRight, setCanTableScrollRight] = React.useState(false);

  React.useLayoutEffect(() => {
    const check = () => {
      const el = tableRef.current;
      if (!el) return;
      setCanTableScrollLeft(el.scrollLeft > 1);
      setCanTableScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    check();
    const el = tableRef.current;
    if (el) el.addEventListener('scroll', check);
    window.addEventListener('resize', check);
    let ro;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(check);
      ro.observe(el);
    }
    return () => {
      if (el) el.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
      if (ro) ro.disconnect();
    };
  }, [offers.length]);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Edge fade overlays — span full table height */}
      {canTableScrollLeft && (
        <Box sx={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 40, zIndex: 3,
          background: 'linear-gradient(to right, white 0%, transparent 100%)',
          pointerEvents: 'none',
          borderRadius: '8px 0 0 8px',
        }} />
      )}
      {canTableScrollRight && (
        <Box sx={{
          position: 'absolute', right: 0, top: 0, bottom: 0, width: 40, zIndex: 3,
          background: 'linear-gradient(to left, white 0%, transparent 100%)',
          pointerEvents: 'none',
          borderRadius: '0 8px 8px 0',
        }} />
      )}

      {/* Sticky arrow overlay — keeps arrows centered in viewport while table is visible */}
      {(canTableScrollLeft || canTableScrollRight) && (
        <Box sx={{
          position: 'sticky',
          top: '50vh',
          height: 0,
          zIndex: 4,
          pointerEvents: 'none',
        }}>
          {canTableScrollLeft && (
            <IconButton
              onClick={() => tableRef.current?.scrollBy({ left: -200, behavior: 'smooth' })}
              size="small"
              sx={{
                position: 'absolute', left: 8, top: 0, transform: 'translateY(-50%)',
                bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                pointerEvents: 'auto',
                '&:hover': { bgcolor: '#f3f4f5' },
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
          )}
          {canTableScrollRight && (
            <IconButton
              onClick={() => tableRef.current?.scrollBy({ left: 200, behavior: 'smooth' })}
              size="small"
              sx={{
                position: 'absolute', right: 8, top: 0, transform: 'translateY(-50%)',
                bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                pointerEvents: 'auto',
                '&:hover': { bgcolor: '#f3f4f5' },
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      <Box
        ref={tableRef}
        sx={{
          overflowX: 'auto',
          borderRadius: 1,
          bgcolor: 'white',
          boxShadow: '0 12px 40px rgba(25,28,29,0.06)',
          border: '1px solid rgba(187,203,184,0.05)',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
      <TableContainer sx={{ overflowX: 'visible' }}>
        <Table sx={{ borderCollapse: 'collapse', minWidth: { md: offers.length > 3 ? 200 + offers.length * 200 : undefined } }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  p: { xs: 1.5, md: 3 },
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', md: '0.8125rem' },
                  color: '#3c4a3c',
                  fontFamily: BODY_FONT,
                  borderBottom: '2px solid #f3f4f5',
                  position: 'sticky',
                  left: 0,
                  bgcolor: 'white',
                  zIndex: 2,
                  minWidth: { xs: 120, md: 180 },
                  boxShadow: '2px 0 4px rgba(0,0,0,0.04)',
                }}
              >
                {t('comparison.product', 'Product')}
              </TableCell>
              {offers.map((offer, i) => (
                <TableCell
                  key={offer.id || i}
                  align="center"
                  sx={{
                    p: { xs: 1.5, md: 3 },
                    fontWeight: 700,
                    fontFamily: HEADING_FONT,
                    fontSize: { xs: '0.75rem', md: '0.9375rem' },
                    borderBottom: '2px solid #f3f4f5',
                    color: '#00658d',
                    minWidth: 140,
                  }}
                >
                  {offer.tab_data?.name || `${t('offers.offer', 'Offer')} ${i + 1}`}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Product rows */}
            {allProducts.map((product) => {
              const Icon = PRODUCT_ICONS[product.type] || Phonelink;
              return (
                <TableRow
                  key={product.key}
                  sx={{ '&:hover': { bgcolor: 'rgba(0,110,42,0.02)' } }}
                >
                  <TableCell
                    sx={{
                      p: { xs: 1.5, md: 2.5 },
                      fontFamily: BODY_FONT,
                      fontSize: { xs: '0.75rem', md: '0.8125rem' },
                      borderBottom: '1px solid #f3f4f5',
                      position: 'sticky',
                      left: 0,
                      bgcolor: 'white',
                      zIndex: 2,
                      boxShadow: '2px 0 4px rgba(0,0,0,0.04)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 } }}>
                      <Icon sx={{ color: '#00658d', fontSize: { xs: 16, md: 18 }, flexShrink: 0 }} />
                      {product.label}
                    </Box>
                  </TableCell>
                  {offerProductMaps.map((productMap, i) => {
                    const item = productMap.get(product.key);
                    if (!item) {
                      return (
                        <TableCell
                          key={i}
                          align="center"
                          sx={{ p: { xs: 1.5, md: 2.5 }, borderBottom: '1px solid #f3f4f5', color: '#ccc', fontFamily: BODY_FONT, fontSize: { xs: '0.75rem', md: '0.8125rem' } }}
                        >
                          —
                        </TableCell>
                      );
                    }
                    const price = item.price || 0;
                    // Pass full item to fmtPrice so phone-numbers in EU get
                    // the correct USD→EUR conversion via priceCurrency.
                    const priceInput = price > 0 ? item : price;
                    const label = item.label || item.name || '';
                    // Extract tier name and quantity from label like "Business Phone Pro (10 users)"
                    const fullMatch = label.match(/^(.+?)\s*\((.+)\)$/);
                    const detail = fullMatch ? fullMatch[2] : null;
                    // Show the tier for products that carry one in their label
                    const tierName = getComparisonTierName(product.type, label);
                    return (
                      <TableCell
                        key={i}
                        align="center"
                        sx={{ p: { xs: 1.5, md: 2.5 }, borderBottom: '1px solid #f3f4f5', fontFamily: BODY_FONT, fontSize: { xs: '0.75rem', md: '0.8125rem' } }}
                      >
                        {price > 0 ? (
                          <Box>
                            <Typography sx={{ fontFamily: BODY_FONT, fontWeight: 600, fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
                              {fmtPrice(priceInput)}
                            </Typography>
                            {tierName && (
                              <Box sx={{
                                display: 'inline-block',
                                mt: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: 'rgba(0,110,42,0.08)',
                                color: '#006e2a',
                                fontFamily: BODY_FONT,
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                              }}>
                                {tierName}
                              </Box>
                            )}
                            {detail && (
                              <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.6875rem', color: '#3c4a3c' }}>
                                {detail}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Box>
                            <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.8125rem', color: '#006e2a', fontWeight: 600 }}>
                              {t('offers.included', 'Included')}
                            </Typography>
                            {tierName && (
                              <Box sx={{
                                display: 'inline-block',
                                mt: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: 'rgba(0,110,42,0.08)',
                                color: '#006e2a',
                                fontFamily: BODY_FONT,
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                              }}>
                                {tierName}
                              </Box>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}

            {/* One-time payments rows — small grey, signal "included in plan below" */}
            {(() => {
              const allDescriptions = [];
              const offerOneTimeMaps = offers.map(offer => {
                const payments = getSnapshotFromOffer(offer)?.one_time_payments || [];
                const map = new Map();
                payments.forEach(p => {
                  if (p.total > 0) {
                    map.set(p.description, p.total);
                    if (!allDescriptions.includes(p.description)) {
                      allDescriptions.push(p.description);
                    }
                  }
                });
                return map;
              });

              if (allDescriptions.length === 0) return null;

              return (
                <>
                  {allDescriptions.map(description => (
                    <TableRow
                      key={`one-time-${description}`}
                      sx={{ '&:hover': { bgcolor: 'rgba(0,110,42,0.02)' } }}
                    >
                      <TableCell
                        sx={{
                          p: { xs: 1.5, md: 2.5 },
                          fontFamily: BODY_FONT,
                          fontSize: { xs: '0.75rem', md: '0.8125rem' },
                          color: 'text.secondary',
                          borderBottom: '1px solid #f3f4f5',
                          position: 'sticky',
                          left: 0,
                          bgcolor: 'white',
                          zIndex: 2,
                          boxShadow: '2px 0 4px rgba(0,0,0,0.04)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 } }}>
                          <RocketLaunch sx={{ color: 'text.secondary', fontSize: { xs: 16, md: 18 }, flexShrink: 0 }} />
                          {description}
                        </Box>
                      </TableCell>
                      {offerOneTimeMaps.map((map, i) => {
                        const hasPayment = map.has(description);
                        return (
                          <TableCell
                            key={i}
                            align="center"
                            sx={{
                              p: { xs: 1.5, md: 2.5 },
                              borderBottom: '1px solid #f3f4f5',
                              fontFamily: BODY_FONT,
                              fontSize: { xs: '0.75rem', md: '0.8125rem' },
                              color: hasPayment ? 'text.secondary' : '#ccc',
                            }}
                          >
                            {hasPayment ? (
                              <Box>
                                <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.875rem', color: 'text.secondary' }}>
                                  {fmtPrice(map.get(description))}
                                </Typography>
                                <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.6875rem', fontStyle: 'italic', color: '#3c4a3c' }}>
                                  {t('comparison.oneTimeAddedToTotal', '*Added to plan below')}
                                </Typography>
                              </Box>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </>
              );
            })()}

            {/* Total row — plain white now that the green tint moved to
                the "Payment Options" header below. */}
            <TableRow>
              <TableCell
                sx={{
                  p: { xs: 1.5, md: 2.5 },
                  fontFamily: HEADING_FONT,
                  fontWeight: 700,
                  fontSize: { xs: '0.8125rem', md: '0.875rem' },
                  borderBottom: '2px solid #f3f4f5',
                  position: 'sticky',
                  left: 0,
                  bgcolor: 'white',
                  zIndex: 2,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.04)',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('comparison.totalMonthly', 'Total / month')}
              </TableCell>
              {offers.map((offer, i) => {
                const snap = getSnapshotFromOffer(offer);
                const total = snap?.total_monthly || offer?.total_price || 0;
                return (
                  <TableCell
                    key={i}
                    align="center"
                    sx={{
                      p: { xs: 1.5, md: 2.5 },
                      fontFamily: HEADING_FONT,
                      fontWeight: 800,
                      fontSize: { xs: '1rem', md: '1.125rem' },
                      color: '#006e2a',
                      borderBottom: '2px solid #f3f4f5',
                    }}
                  >
                    {fmtPrice(total)}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Subscription plan rows */}
            {offerPlans.some(op => op.plans.length > 0) && (
              <>
                {/* Section header — the brand-green tint that used to live
                    on the "Total / month" row now anchors the Payment
                    Options block instead. */}
                <TableRow sx={{ bgcolor: 'rgba(0,110,42,0.03)' }}>
                  <TableCell
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      fontFamily: HEADING_FONT,
                      borderBottom: '1px solid #d7ecdd',
                      position: 'sticky',
                      left: 0,
                      bgcolor: '#f2f8f3',
                      zIndex: 2,
                      boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CreditCard sx={{ color: '#1f2937', fontSize: { xs: 18, md: 22 } }} />
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: HEADING_FONT,
                          fontWeight: 800,
                          fontSize: { xs: '0.9375rem', md: '1.125rem' },
                          color: '#1f2937',
                          letterSpacing: '0.2px',
                        }}
                      >
                        {t('offer.subscriptionOptions', 'Payment Options')}
                      </Typography>
                    </Box>
                  </TableCell>
                  {offers.map((_, i) => (
                    <TableCell
                      key={i}
                      sx={{ borderBottom: '1px solid #d7ecdd' }}
                    />
                  ))}
                </TableRow>
                {(() => {
                  const allPlanIds = new Set();
                  offerPlans.forEach(op => op.plans.forEach(p => allPlanIds.add(p.id)));
                  // Only highlight a "recommended" plan when there are
                  // alternatives to compare it against — a single-row
                  // table doesn't need a winner.
                  const hasMultiplePlans = allPlanIds.size > 1;
                  return Array.from(allPlanIds).map(planId => {
                    const planConfig = offerPlans.find(op => op.plans.some(p => p.id === planId)) || {};
                    // Treat a plan as recommended for the label badge if any
                    // offer marks it as such (typically the 360-day bonus
                    // plan) AND there are other plans to compare against.
                    const isPlanRecommended = hasMultiplePlans && offerPlans.some(op =>
                      op.plans.some(p => p.id === planId && (p.isRecommended || p.is_recommended))
                    );
                    // The "Recommended" chip alone is enough emphasis —
                    // no extra row frame.
                    const recBorderTop = undefined;
                    const recBorderBottom = '1px solid #f3f4f5';
                    return (
                      <TableRow key={planId} sx={{ '&:hover': { bgcolor: 'rgba(0,110,42,0.02)' } }}>
                        <TableCell
                          sx={{
                            p: { xs: 1.5, md: 2.5 },
                            fontFamily: BODY_FONT,
                            fontSize: { xs: '0.75rem', md: '0.8125rem' },
                            borderTop: recBorderTop,
                            borderBottom: recBorderBottom,
                            position: 'sticky',
                            left: 0,
                            bgcolor: 'white',
                            zIndex: 2,
                            boxShadow: '2px 0 4px rgba(0,0,0,0.04)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            {getComparisonPlanLabel(planId, {
                              hasMultiplePlans,
                              customDiscount360: planConfig.customDiscount360,
                              customDiscount180: planConfig.customDiscount180,
                              t,
                            })}
                            {planId === '360-days-split' && (
                              <>
                                <MonobankIcon size={14} />
                                <ABankIcon size={14} />
                              </>
                            )}
                            {isPlanRecommended && (
                              <Chip
                                size="small"
                                label={t('offer.recommended', 'Recommended')}
                                sx={{
                                  bgcolor: '#d9f0e0',
                                  color: '#006e2a',
                                  fontFamily: HEADING_FONT,
                                  fontWeight: 700,
                                  fontSize: '0.625rem',
                                  letterSpacing: '0.3px',
                                  height: 20,
                                  '& .MuiChip-label': { px: 1 },
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        {offerPlans.map((offerData, i) => {
                          const plan = offerData.plans.find(p => p.id === planId);
                          if (!plan) {
                            return (
                              <TableCell
                                key={i}
                                align="center"
                                sx={{
                                  p: { xs: 1.5, md: 2.5 },
                                  borderTop: recBorderTop,
                                  borderBottom: recBorderBottom,
                                  color: '#ccc',
                                  fontFamily: BODY_FONT,
                                }}
                              >
                                —
                              </TableCell>
                            );
                          }
                          const payAmount = plan.paymentAmount || plan.payment_amount || 0;
                          const monthPrice = plan.monthlyPrice || plan.monthly_price || 0;
                          const savings = getComparisonPlanSavings(plan, hasMultiplePlans);
                          const vatAmount = isUa && payAmount ? payAmount * 1.2 : null;

                          return (
                            <TableCell
                              key={i}
                              align="center"
                              sx={{
                                p: { xs: 1.5, md: 2.5 },
                                borderTop: recBorderTop,
                                borderBottom: recBorderBottom,
                                fontFamily: BODY_FONT,
                              }}
                            >
                              {payAmount > 0 ? (
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontFamily: BODY_FONT, fontWeight: 700, fontSize: '0.875rem' }}>
                                      {fmtPrice(payAmount)}
                                    </Typography>
                                    {savings > 0 && (
                                      <Chip
                                        size="small"
                                        label={`${t('subscription.savings', 'Savings')} ${fmtPrice(savings)}`}
                                        sx={{
                                          height: 20,
                                          bgcolor: 'rgba(0, 200, 83, 0.10)',
                                          color: '#006e2a',
                                          fontFamily: BODY_FONT,
                                          fontWeight: 700,
                                          fontSize: '0.625rem',
                                          '& .MuiChip-label': { px: 0.75 },
                                        }}
                                      />
                                    )}
                                  </Box>
                                  {isUa && vatAmount && (
                                    <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.6875rem', color: '#3c4a3c' }}>
                                      {t('subscription.vatIncluded', 'Including VAT 20%: {{amount}}', { amount: fmtPrice(vatAmount) })}
                                    </Typography>
                                  )}
                                  {monthPrice > 0 && (
                                    <Typography sx={{
                                      fontFamily: HEADING_FONT,
                                      fontWeight: 700,
                                      fontSize: '0.8125rem',
                                      mt: 0.5,
                                      background: 'linear-gradient(135deg, #006e2a 0%, #00a040 100%)',
                                      WebkitBackgroundClip: 'text',
                                      WebkitTextFillColor: 'transparent',
                                      backgroundClip: 'text',
                                    }}>
                                      {fmtPrice(monthPrice)}{t('offer.perMonth', '/mo')}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.8125rem', color: '#3c4a3c' }}>
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  });
                })()}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>
    </Box>
  );
};

// ---------- Inner Component ----------
const ComparisonPageInner = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { displayCurrency, updatePreferences } = useUserPreferences();
  const { currencyRates } = usePricing();
  const { isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  useEffect(() => {
    if (comparison?.language && comparison.language !== i18n.language) {
      i18n.changeLanguage(comparison.language);
    }
  }, [comparison?.language, i18n]);

  useEffect(() => {
    const fetchComparison = async () => {
      try {
        setLoading(true);
        const response = await getComparison(id);
        setComparison(response.data);
      } catch (err) {
        console.error('Error fetching comparison:', err);
        setError(err.response?.data?.error || 'Failed to load comparison');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchComparison();
  }, [id]);

  const offers = comparison?.offers || [];
  const firstOffer = offers[0];
  const firstSnapshot = getSnapshotFromOffer(firstOffer);

  const region = firstSnapshot?.region;
  const rates = firstSnapshot?.currencyRates || currencyRates;
  // Smart formatter: accepts either a number (aggregate) or a normalized
  // snapshot item with `priceCurrency`. Legacy snapshots (no priceCurrency)
  // are handled by formatSnapshotPrice's fallback so old comparisons keep
  // rendering correctly.
  const fmtPrice = (input) => formatSnapshotPrice(input, { region, displayCurrency, rates });

  const salesContact = firstOffer?.sales_contact || {
    name: firstOffer?.metadata?.generator_user_name || '',
    email: firstOffer?.metadata?.generator_user_email || '',
    phone: null,
    photo_url: null,
  };

  const handleExportPDF = async () => {
    if (!comparison || !offers.length) return;
    const clientName = comparison?.metadata?.client_name || '';
    const validDate = getOfferValidityDate(comparison?.created_at);
    const filename = generatePDFFilename('comparison', '', clientName, validDate, false);
    await exportToPDF('comparison-v3-container', filename, 'comparison', offers.length);
  };

  const handleUseAsTemplate = async () => {
    const payloads = buildComparisonTemplatePayloads(comparison);
    if (!payloads.length) return;

    const calculatorWindow = window.open('', '_blank');
    setTemplateLoading(true);
    try {
      const createdIds = [];
      for (const payload of payloads) {
        const response = await createTab(payload);
        const newTabId = response.data?.id;
        if (newTabId) createdIds.push(newTabId);
      }

      const templateRegion = getComparisonTemplateRegion(comparison);
      if (templateRegion) {
        await updatePreferences({ region: templateRegion });
      }

      if (createdIds.length > 0) {
        const templateUrl = buildCalculatorTemplateUrl(createdIds[0]);
        if (calculatorWindow) {
          calculatorWindow.opener = null;
          calculatorWindow.location.href = templateUrl;
        } else {
          window.open(templateUrl, '_blank', 'noopener,noreferrer');
        }
      } else if (calculatorWindow) {
        calculatorWindow.close();
      }
    } catch (err) {
      if (calculatorWindow) {
        calculatorWindow.close();
      }
      console.error('Failed to create comparison template tabs:', err);
    } finally {
      setTemplateLoading(false);
    }
  };

  const clientName = comparison?.metadata?.client_name || comparison?.title || '';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#006e2a' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error" variant="h6">{error}</Typography>
      </Container>
    );
  }

  if (!comparison || !offers.length) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="text.secondary" variant="h6">
          {t('comparison.noData', 'No comparison data found')}
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <LandingHeader
        offer={firstOffer}
        onExportPDF={handleExportPDF}
        onUseAsTemplate={isAuthenticated ? handleUseAsTemplate : undefined}
        useAsTemplateDisabled={buildComparisonTemplatePayloads(comparison).length === 0}
        templateLoading={templateLoading}
      />

      <Container id="comparison-v3-container" maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* 1. Hero + Sales Card */}
        <Box
          data-pdf-section="hero"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { lg: 'stretch' },
            gap: 4,
            mb: 10,
          }}
        >
          <Box
            sx={{
              flex: 3,
              background: 'linear-gradient(160deg, #004d1a 0%, #006e2a 50%, #00c853 100%)',
              color: '#fff',
              p: { xs: 4, md: 5 },
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              '&:hover': { boxShadow: '0 10px 40px rgba(0,0,0,0.15)' },
            }}
          >
            <Box>
              <Box
                sx={{
                  display: 'inline-flex', px: 2, py: 0.75, mb: 3, borderRadius: 99,
                  bgcolor: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(4px)',
                  fontFamily: BODY_FONT, fontSize: '0.875rem', fontWeight: 700,
                }}
              >
                {t('offer.validUntil', 'Valid until {{date}}', { date: getOfferValidityDate(comparison?.created_at) })}
              </Box>

              <Typography sx={{ fontFamily: HEADING_FONT, fontWeight: 800, mb: 2, fontSize: { xs: '2rem', md: '2.5rem' }, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                {clientName
                  ? <>{t('offers.offersComparisonFor', 'Offer Comparison for {{companyName}}', { companyName: '' })}<Box component="span" sx={{ color: 'rgba(255,255,255,0.7)' }}>{clientName}</Box></>
                  : t('offers.offersComparison', 'Offer Comparison')}
              </Typography>

              <Typography sx={{ fontFamily: BODY_FONT, color: 'rgba(255,255,255,0.8)', fontSize: '1.125rem', maxWidth: 560, lineHeight: 1.7 }}>
                {t('comparison.heroDescription', 'Choose the optimal solution for your business from the proposed options.')}
              </Typography>
            </Box>

            <Box sx={{ mt: 'auto', pt: 4 }}>
              <Typography sx={{ fontFamily: BODY_FONT, color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>
                {t('comparison.variants', '{{count}} options', { count: offers.length })}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 2, display: 'flex', justifyContent: { lg: 'flex-end' } }}>
            <SalesContactCard
              salesContact={salesContact}
              variant={isDesktop ? 'full' : 'compact'}
              sx={{ width: '100%', maxWidth: { lg: 380 } }}
            />
          </Box>
        </Box>

        {/* 2. Summary Cards */}
        <SectionTitle>{t('comparison.yourOptions', 'Your Options')}</SectionTitle>
        <Box
          data-pdf-section="summary-cards"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: `repeat(${offers.length <= 3 ? offers.length : offers.length === 4 ? 4 : 3}, 1fr)`,
              lg: `repeat(${offers.length <= 4 ? offers.length : offers.length <= 6 ? 3 : 4}, 1fr)`,
            },
            gap: 3,
            mb: 10,
          }}
        >
          {offers.map((offer, index) => (
            <SummaryCard key={offer.id || index} offer={offer} index={index} t={t} fmtPrice={fmtPrice} />
          ))}
        </Box>

        {/* 3. Comparison Table */}
        <SectionTitle>{t('comparison.detailedComparison', 'Detailed Comparison')}</SectionTitle>
        <Box data-pdf-section="comparison-table" sx={{ mb: 10 }}>
          <OfferComparisonTable offers={offers} t={t} fmtPrice={fmtPrice} />
        </Box>

        {/* 4. Advantages */}
        <Box data-pdf-section="advantages" sx={{ mb: 10 }}>
          <AdvantagesSection />
        </Box>

        {/* 5. Contact Footer */}
        <Box data-pdf-section="footer">
          <ContactFooter salesContact={salesContact} />
        </Box>
      </Container>
    </Box>
  );
};

const ComparisonPage = () => (
  <UserPreferencesProvider controlLanguage={false}>
    <PricingProvider>
      <ComparisonPageInner />
    </PricingProvider>
  </UserPreferencesProvider>
);

export default ComparisonPage;
